/**
 * FORGE-400 — Trusted-origin allow-list for scripts injected from OrderPage
 * rich content (OrderPageItemChoice__c.Argument1__c).
 *
 * Why this exists:
 *   `RichOfferContent.vue` renders the saved HTML with `v-html`, which DOES
 *   NOT execute embedded `<script>` tags (DOM `innerHTML` standard behaviour).
 *   To support trusted external embeds (Vidyard, Calendly, GTM, …) we extract
 *   those scripts and inject them programmatically as `document.createElement
 *   ('script')` elements at `mounted()` time. Untrusted scripts and inline
 *   scripts (no src) are dropped here as defence-in-depth — the Apex
 *   `OrderPageConfigController.sanitizeScripts` already strips them at save
 *   time, but we re-validate at render in case stale content was saved before
 *   the Apex sanitization shipped, or a future migration imports content from
 *   another source.
 *
 *   Origin matching uses anchored regexes against the script's `src` URL.
 *
 *   ════════════════════════════════════════════════════════════════════════
 *   ⚠️  MUST STAY IN SYNC WITH the Apex side — both sides validate the same
 *      list independently:
 *
 *      Server (authoritative):
 *        - salesforce-apex/customMetadata/
 *            Simple_Parameter__mdt.orderPageTrustedScriptOrigins.md-meta.xml
 *        - salesforce-apex/classes/OrderPageConfigController.cls
 *            (sanitizeScripts → getTrustedScriptOriginPatterns)
 *
 *      Client (this file, defense-in-depth):
 *        - vanguard-v2/src/services/scriptOriginAllowList.js
 *            (TRUSTED_PATTERNS below)
 *        - vanguard-v2/tests/unit/services/scriptOriginAllowList.test.js
 *            (smoke list "expected production origins" — guarantees that
 *             adding/removing an origin here forces an update on the test
 *             list, surfacing a drift between Apex and JS during CI review)
 *
 *      A new trusted origin (e.g. Calendly, GTM) MUST be added in all four
 *      locations in the same change set. The smoke test will fail if the
 *      JS side falls behind.
 *   ════════════════════════════════════════════════════════════════════════
 *
 *   Pubfac PR #1279 review — drift detection.
 */

const TRUSTED_PATTERNS = [
  // SYNC_WITH: Simple_Parameter__mdt.orderPageTrustedScriptOrigins
  // Vidyard — video player CDN. Covers embed/v4.js + lazy-loaded chunks.
  /^https:\/\/play\.vidyard\.com\//,
  // Add new trusted origins here. Keep them anchored with `^` so a query string
  // like `?ref=https://play.vidyard.com/` cannot bypass the allow-list.
  // Whenever you add an origin here, also update:
  //   - Apex CMDT seed XML (canonical)
  //   - tests/unit/services/scriptOriginAllowList.test.js EXPECTED_PROD_ORIGINS
];

/**
 * Returns true when `src` is a non-empty string whose URL matches one of the
 * trusted origin patterns. Inline scripts (no src) always return false.
 *
 * @param {string} src
 * @returns {boolean}
 */
export function isScriptSrcTrusted(src) {
  if (!src || typeof src !== "string") return false;
  return TRUSTED_PATTERNS.some((p) => p.test(src));
}

/**
 * Extracts every `<script src="…">` tag from the given rich-content HTML
 * whose origin matches the trusted allow-list. Inline scripts and untrusted
 * origins are dropped silently.
 *
 * The returned descriptors carry the small subset of attributes that matter
 * for typical embed scripts (Vidyard, Calendly, GTM): src, async, defer,
 * type, crossorigin. The consumer (`RichOfferContent.vue`) recreates real
 * `<script>` elements with these attributes inside `mounted()`.
 *
 * Implementation: regex over the raw HTML rather than `DOMParser`. Two
 * reasons:
 *
 *   1. The browser HTML5 spec says `DOMParser` MUST NOT execute scripts when
 *      the MIME type is "text/html" — but `happy-dom` (the Vitest DOM impl
 *      we use) DOES execute them, which would attempt a network fetch on
 *      `<script src="…">` and run inline code during a unit test. Regex
 *      sidesteps that landmine and stays consistent with the Apex
 *      `OrderPageConfigController.sanitizeScripts` which is regex-based.
 *
 *   2. We never need a full DOM tree for this — only a list of script tags.
 *      A regex pass is cheap and predictable across environments (browser,
 *      Vitest, jsdom, SSR if we ever add it).
 *
 * @param {string} html
 * @returns {Array<{src: string, async: boolean, defer: boolean, type: (string|null), crossorigin: (string|null)}>}
 */
const SCRIPT_BLOCK_REGEX = /<script\b([^>]*)>([\s\S]*?)<\/script>/gi;
const SRC_ATTR_REGEX = /\bsrc\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i;
const TYPE_ATTR_REGEX = /\btype\s*=\s*(?:"([^"]+)"|'([^']+)'|([^\s>]+))/i;
const CROSSORIGIN_ATTR_REGEX =
  /\bcrossorigin(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/i;

function getAttr(attrsString, regex) {
  const m = regex.exec(attrsString);
  if (!m) return null;
  return m[1] ?? m[2] ?? m[3] ?? "";
}

export function extractTrustedScripts(html) {
  if (!html || typeof html !== "string") return [];

  const out = [];
  // Reset module-level /g regex lastIndex before iterating.
  SCRIPT_BLOCK_REGEX.lastIndex = 0;
  let m;
  while ((m = SCRIPT_BLOCK_REGEX.exec(html)) !== null) {
    const attrs = m[1] || "";
    const src = getAttr(attrs, SRC_ATTR_REGEX) || "";
    if (!isScriptSrcTrusted(src)) continue;
    out.push({
      src,
      async: /\basync\b/i.test(attrs),
      defer: /\bdefer\b/i.test(attrs),
      type: getAttr(attrs, TYPE_ATTR_REGEX),
      crossorigin: getAttr(attrs, CROSSORIGIN_ATTR_REGEX),
    });
  }
  return out;
}
